import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { userActions } from "store/user/user.slice";

function PrivacyPolicy({
  isAsk,
  country,
}: {
  isAsk?: boolean;
  country?: string;
}) {
  const dispatch = useDispatch();
  const myElement = document.getElementById("isAsk");

  useEffect(() => {
    if (isAsk === true) {
      myElement?.scrollIntoView({
        behavior: "smooth",
      });
    }

    if (country) dispatch(userActions.setCountry(country!));
  });

  // src/pages/PrivacyPolicy.tsx or similar path

  return (
    <section className="relative pb-32 pt-20 lg:px-32 pt-20 w-full">
      <h1 className="text-3xl font-bold mb-6">Vhicasar Homes Privacy Policy</h1>
      <p className="mb-2">
        Effective Date: <strong>July 29, 2025</strong>
      </p>
      <p className="mb-6">
        Last Updated: <strong>July 29, 2025</strong>
      </p>

      <p className="mb-6">
        At Vhicasar Homes, your privacy is important to us. This Privacy Policy
        explains how we collect, use, and protect your personal information when
        you use our mobile application.
      </p>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          1. Information We Collect
        </h2>
        <p className="mb-2">
          When you use Vhicasar Homes, we may collect the following information:
        </p>
        <ul className="list-disc list-inside ml-4">
          <li className="mb-1">
            <strong>Personal Information:</strong>
            <ul className="list-disc list-inside ml-6">
              <li>Full Name</li>
              <li>Email Address</li>
              <li>Phone Number</li>
              <li>Location (used to show nearby services)</li>
              <li>
                Any other information you provide during registration or use of
                the app
              </li>
            </ul>
          </li>
          <li className="mb-1">
            <strong>Usage Information:</strong>
            <ul className="list-disc list-inside ml-6">
              <li>Device information (model, OS, etc.)</li>
              <li>App usage data (pages viewed, interactions)</li>
              <li>Communication data (chat messages sent within the app)</li>
            </ul>
          </li>
          <li>
            <strong>Location Data:</strong>
            <ul className="list-disc list-inside ml-6">
              <li>
                We use your device's GPS or network location to suggest services
                around you.
              </li>
              <li>
                You can disable location access in your phone settings, but
                doing so may affect core app functionality.
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          2. How We Use Your Information
        </h2>
        <ul className="list-disc list-inside ml-4">
          <li>Create and manage your account</li>
          <li>Provide personalized service recommendations</li>
          <li>Facilitate communication between users and vendors</li>
          <li>Improve our app and services</li>
          <li>Send you relevant notifications and updates</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          3. Data Sharing and Disclosure
        </h2>
        <p className="mb-2">
          We do not sell or rent your personal data. We may share information
          with:
        </p>
        <ul className="list-disc list-inside ml-4">
          <li>
            Service providers/vendors when you interact with their services
          </li>
          <li>
            Third-party service integrations (e.g., Google Maps for location,
            Firebase for push notifications)
          </li>
          <li>Authorities or law enforcement when required by law</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          4. Data Storage and Security
        </h2>
        <p>
          We use secure servers and encryption methods to protect your data.
          While we implement industry-standard security measures, no method of
          transmission over the internet is 100% secure.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">5. Your Rights</h2>
        <p className="mb-2">You have the right to:</p>
        <ul className="list-disc list-inside ml-4">
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your account and associated data</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p className="mt-2">
          To exercise these rights, contact us at: <br />
          Email:{" "}
          <a
            href="mailto:support@vhicasar.com"
            className="text-blue-600 underline"
          >
            support@vhicasar.com
          </a>
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">6. Data Retention</h2>
        <p>
          We retain your data for as long as your account is active or as needed
          to provide services. We may also retain certain information to comply
          with legal obligations or resolve disputes.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">7. Children’s Privacy</h2>
        <p>
          Vhicasar Homes is not specifically directed at children under 13. If we
          learn that we have collected personal data from a child without
          parental consent, we will delete that information promptly.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">8. Third-Party Services</h2>
        <p>
          We use third-party tools that may collect data according to their own
          privacy policies. These include:
        </p>
        <ul className="list-disc list-inside ml-4">
          <li>Google Maps (Location services)</li>
          <li>Firebase Cloud Messaging (Push notifications)</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          9. Changes to this Policy
        </h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify
          you of any significant changes by email or through the app.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">10. Contact Us</h2>
        <p>For questions or concerns regarding this Privacy Policy, contact:</p>
        <p>
          Email:{" "}
          <a
            href="mailto:support@vhicasar.com"
            className="text-blue-600 underline"
          >
            support@vhicasar.com
          </a>
        </p>
      </section>
    </section>
  );
}

export default PrivacyPolicy;
